import React, { useEffect, useMemo, useState } from "react";
import API from "../../api";
import "../css/CreateTicket.css";

/** redux-persist first -> token fallback (same approach as your ShowDocumnetModal) */
function getUserFromToken() {
  const tok =
    window.localStorage.getItem("token") ||
    window.sessionStorage.getItem("token");
  if (!tok) return {};
  const parts = tok.split(".");
  if (parts.length < 2) return {};
  try {
    const json = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return json?.user || json || {};
  } catch {
    return {};
  }
}

function getUsernameAndRole() {
  const keysToTry = ["persist:root", "persistroot"];
  for (const key of keysToTry) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;

      const root = JSON.parse(raw);
      const authObj =
        typeof root?.auth === "string" ? JSON.parse(root.auth) : root?.auth;

      const username =
        authObj?.user?.username ||
        authObj?.user?.name ||
        authObj?.username ||
        "";

      const role = (authObj?.user?.role || authObj?.role || "").toLowerCase();

      if (username || role) {
        return { username: String(username || ""), role: String(role || "") };
      }
    } catch {}
  }

  const u = getUserFromToken();
  const usernameFromToken =
    u?.username || u?.name || u?.preferred_username || "";
  const roleFromToken = (u?.role || "").toLowerCase();

  return {
    username: String(usernameFromToken || ""),
    role: String(roleFromToken || ""),
  };
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  try {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function FieldRow({ label, value }) {
  return (
    <div className="ct-row">
      <div className="ct-label">{label}</div>
      <div className="ct-value">{String(value ?? "").trim() ? value : "—"}</div>
    </div>
  );
}

export default function CreateTicket({
  open,
  onClose,
  mpa,          // pass full MPA row here (includes merchant_id)
  onCreated,    // callback after create
}) {
  const { username, role } = useMemo(() => getUsernameAndRole(), []);
  const merchantId = useMemo(
    () => mpa?.merchant_id || mpa?.merchantId || "",
    [mpa]
  );

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("technical_issue");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [bannerErr, setBannerErr] = useState("");
  const [bannerOk, setBannerOk] = useState("");

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setCategory("technical_issue");
    setDescription("");
    setBannerErr("");
    setBannerOk("");
    setSaving(false);
  }, [open]);

  async function createTicket() {
    setBannerErr("");
    setBannerOk("");

    if (!merchantId) {
      setBannerErr("Missing merchant_id.");
      return;
    }
    if (!username) {
      setBannerErr("Missing username.");
      return;
    }
    if (!title.trim()) {
      setBannerErr("Title is required.");
      return;
    }
    if (!description.trim()) {
      setBannerErr("Please enter the detailed problem.");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        merchant_id: merchantId,
        username,
        role,
        title: title.trim(),
        category,
        description: description.trim(),
      };

      const resp = await API.post("/auth/ticket-create", payload);

      setBannerOk(`Ticket created (ID: ${resp?.data?.ticket_id || "—"})`);
      setTimeout(() => setBannerOk(""), 1800);

      onCreated?.(resp?.data);
      onClose?.();
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Create ticket failed";
      setBannerErr(msg);
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div className="ct-backdrop" onClick={onClose} role="presentation">
      <div
        className={`ct-drawer ${open ? "ct-open" : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="ct-header">
          <div className="ct-header-title">
            <div className="ct-title">
              Create Support Ticket
            </div>
            <div className="ct-sub">
              <span>Merchant ID: {merchantId || "—"}</span>
            </div>
          </div>

          <div className="ct-header-actions">
            {bannerErr && <span className="ct-banner ct-banner-error">{bannerErr}</span>}
            {bannerOk && <span className="ct-banner ct-banner-ok">{bannerOk}</span>}
            <button className="ct-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        <div className="ct-body">
          {/* OWNER DETAILS */}
          <div className="ct-section">
            <div className="ct-section-title">Owner Details</div>
            <div className="ct-grid">
              <FieldRow label="Owner Name" value={mpa?.name} />
              <FieldRow label="Email" value={mpa?.email} />
              <FieldRow label="Phone" value={mpa?.phone} />
              <FieldRow label="DOB" value={mpa?.DOB} />
              <FieldRow label="Owner Street" value={mpa?.street} />
              <FieldRow label="City" value={mpa?.city} />
              <FieldRow label="State" value={mpa?.state} />
              <FieldRow label="Postal Code" value={mpa?.postal_code} />
            </div>
          </div>

          {/* BUSINESS DETAILS */}
          <div className="ct-section">
            <div className="ct-section-title">Business Details</div>
            <div className="ct-grid">
              <FieldRow label="Business Name" value={mpa?.bname} />
              <FieldRow label="Legal Name" value={mpa?.["Legal-Name"] || mpa?.LegalName} />
              <FieldRow label="DBA Name" value={mpa?.["DBA-Name"] || mpa?.DBAName} />
              <FieldRow label="Tax ID" value={mpa?.["Tax-id"] || mpa?.TaxId} />
              <FieldRow label="Website" value={mpa?.Website} />
              <FieldRow label="Ownership Type" value={mpa?.["ownership-type"]} />
              <FieldRow label="Business Address" value={mpa?.["Business-Address"]} />
              <FieldRow label="Business City" value={mpa?.["Business-City"]} />
              <FieldRow label="Business State" value={mpa?.["Business-State"]} />
              <FieldRow label="Business Zip" value={mpa?.["Business-zip"]} />
              <FieldRow label="Business Start Date" value={mpa?.["Business-start-date"]} />
              <FieldRow label="Avg Ticket" value={mpa?.["Avg-ticket"]} />
              <FieldRow label="High Ticket" value={mpa?.["High-ticket"]} />
              <FieldRow label="Total Monthly Processing" value={mpa?.["total-monthly-processing"]} />
            </div>
          </div>

          {/* BANKING DETAILS */}
          <div className="ct-section">
            <div className="ct-section-title">Banking Details</div>
            <div className="ct-grid">
              <FieldRow label="Bank Name" value={mpa?.["Bank-name"]} />
              <FieldRow label="Bank Account" value={mpa?.["Bank-acc"]} />
              <FieldRow label="Routing" value={mpa?.Routing} />
            </div>
          </div>

          {/* EQUIPMENT DETAILS */}
          <div className="ct-section">
            <div className="ct-section-title">Equipment Details</div>
            <div className="ct-grid">
              <FieldRow label="Equipment Selected" value={mpa?.equipement} />
              <FieldRow label="PAX A920" value={mpa?.paxa920} />
              <FieldRow label="Dejavoo QD4" value={mpa?.["dejavoo-qd4"]} />
              <FieldRow label="Clover Go" value={mpa?.["clover-go"]} />
              <FieldRow label="Clover Flex" value={mpa?.["clover-flex"]} />
              <FieldRow label="Clover Mini" value={mpa?.["clover-mini"]} />
              <FieldRow label="Clover Station" value={mpa?.["clover-station"]} />
              <FieldRow label="Clover Station Duo" value={mpa?.["clover-station-duo"]} />

              <FieldRow label="Shipping Address" value={mpa?.["Shipping-Address"]} />
              <FieldRow label="Shipping City" value={mpa?.["Shipping-City"]} />
              <FieldRow label="Shipping State" value={mpa?.["Shipping-State"]} />
              <FieldRow label="Shipping Zip" value={mpa?.["Shipping-zip"]} />
            </div>
          </div>

          {/* Small meta */}
          <div className="ct-meta">
            <span>Logged in as: <b>{username || "—"}</b> ({role || "—"})</span>
            <span>MPA Updated: <b>{formatDateTime(mpa?.updated_at)}</b></span>
          </div>
        </div>

        {/* Footer: ticket form */}
        <div className="ct-footer">
          <div className="ct-footer-grid">
            <div className="ct-field">
              <label>Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Short title (e.g., Terminal not connecting)"
              />
            </div>

            <div className="ct-field">
              <label>Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="technical_issue">Technical issue</option>
                <option value="customer_service">Customer Service</option>
                <option value="bank_info_change">Bank Info Change</option>
                <option value="setup_call">Setup call</option>
              </select>
            </div>

            <div className="ct-field ct-span-2">
              <label>Detailed problem</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the issue in detail…"
                rows={4}
              />
            </div>
          </div>

          <div className="ct-footer-actions">
            <button className="ct-btn" onClick={onClose} disabled={saving}>
              Close
            </button>
            <button className="ct-btn ct-btn-primary" onClick={createTicket} disabled={saving}>
              {saving ? "Creating…" : "Create Ticket"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
